import React, { useEffect, useMemo, useState } from 'react';
import { useAssessment, DEFAULT_LATE_POLICY } from './SharedContext';
import { Shield, Clock, Plus, Trash } from 'lucide-react';

const CourseSettings = ({ embedded = false, title = 'Course Settings' }) => {
  const {
    gradingData,
    courseLatePolicies,
    saveCourseLatePolicy,
    deleteCourseLatePolicy,
    getEffectiveLatePolicy,
  } = useAssessment();

  const initialCourseCode = gradingData?.course?.code || '';
  const [courseId, setCourseId] = useState(initialCourseCode);
  const [isEnabled, setIsEnabled] = useState(true);
  const [requiresExtension, setRequiresExtension] = useState(true);
  const [maxLateDays, setMaxLateDays] = useState(DEFAULT_LATE_POLICY.maxLateDays);
  const [penalties, setPenalties] = useState([
    { day: 1, penalty: 0.2 },
    { day: 2, penalty: 0.4 },
    { day: 3, penalty: 1.0 },
  ]);

  const activePolicy = useMemo(() => getEffectiveLatePolicy(courseId), [courseId, getEffectiveLatePolicy]);

  useEffect(() => {
    if (activePolicy) {
      setIsEnabled(activePolicy.enabled);
      setRequiresExtension(activePolicy.requiresApprovedExtension24hBefore);
      setMaxLateDays(activePolicy.maxLateDays);
      const sortedPenalties = Object.entries(activePolicy.penaltiesByDay || {})
        .map(([day, penalty]) => ({ day: Number(day), penalty }))
        .sort((a, b) => a.day - b.day);
      if (sortedPenalties.length) {
        setPenalties(sortedPenalties);
      }
    }
  }, [activePolicy]);

  const addPenaltyRow = () => {
    const nextDay = penalties.length ? Math.max(...penalties.map(p => p.day)) + 1 : 1;
    setPenalties([...penalties, { day: nextDay, penalty: 0 }]);
  };

  const updatePenaltyRow = (index, field, value) => {
    setPenalties(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const removePenaltyRow = (index) => {
    setPenalties(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!courseId) {
      alert('Enter a course code to save a late policy.');
      return;
    }

    if (!Number.isInteger(Number(maxLateDays)) || Number(maxLateDays) <= 0) {
      alert('Max late days must be a positive integer.');
      return;
    }

    const cleanedPenalties = {};
    for (const row of penalties) {
      const day = Number(row.day);
      const penaltyValue = Number(row.penalty);
      if (!Number.isInteger(day) || day <= 0) {
        alert('Penalty days must be positive integers.');
        return;
      }
      if (penaltyValue < 0 || penaltyValue > 1) {
        alert('Penalty percentages must be between 0 and 1.');
        return;
      }
      cleanedPenalties[day] = penaltyValue;
    }

    saveCourseLatePolicy(courseId, {
      courseId,
      enabled: isEnabled,
      requiresApprovedExtension24hBefore: requiresExtension,
      maxLateDays: Number(maxLateDays),
      penaltiesByDay: cleanedPenalties,
      name: `${courseId} Late Policy`,
      description: DEFAULT_LATE_POLICY.description,
    });

    alert('Late policy saved for this course.');
  };

  const handleDelete = () => {
    if (courseId && courseLatePolicies[courseId]) {
      deleteCourseLatePolicy(courseId);
      alert('Course-specific late policy removed. The default policy will apply.');
    }
  };

  const containerClass = embedded ? 'space-y-6' : 'max-w-5xl mx-auto p-6 space-y-6';

  return (
    <div className={containerClass}>
      <div className="flex items-center gap-3">
        <Shield className="text-indigo-600" />
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <p className="text-gray-600">Manage course-specific late policies or rely on the default college standard.</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Course Code</label>
            <input
              type="text"
              value={courseId}
              onChange={(e) => setCourseId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., COMP1234"
            />
          </div>
          <div className="flex items-center gap-3 mt-6 md:mt-0">
            <input
              id="late-policy-enabled"
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
            />
            <label htmlFor="late-policy-enabled" className="text-sm text-gray-800">Enable late policy for this course</label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Max Late Days</label>
            <input
              type="number"
              min={1}
              value={maxLateDays}
              onChange={(e) => setMaxLateDays(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-gray-500">Days beyond this use the highest penalty.</p>
          </div>
          <div className="space-y-2 md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Extension Requirement</label>
            <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
              <input
                type="checkbox"
                checked={requiresExtension}
                onChange={(e) => setRequiresExtension(e.target.checked)}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-800">Extension must be approved at least 24 hours before the due date to waive penalties.</span>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-800 font-medium">
              <Clock className="h-4 w-4 text-indigo-600" /> Late Penalty Schedule
            </div>
            <button
              type="button"
              onClick={addPenaltyRow}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4" /> Add Day
            </button>
          </div>

          <div className="space-y-2">
            {penalties.map((row, index) => (
              <div key={`${row.day}-${index}`} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center border border-gray-200 rounded-lg p-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600">Day</label>
                  <input
                    type="number"
                    min={1}
                    value={row.day}
                    onChange={(e) => updatePenaltyRow(index, 'day', Number(e.target.value))}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">Penalty (0 - 1)</label>
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    value={row.penalty}
                    onChange={(e) => updatePenaltyRow(index, 'penalty', Number(e.target.value))}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => removePenaltyRow(index)}
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                    aria-label="Remove penalty row"
                  >
                    <Trash className="h-4 w-4" /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700"
          >
            Save Late Policy
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
          >
            Remove Course Policy
          </button>
        </div>

        <div className="text-sm text-gray-600 bg-indigo-50 border border-indigo-100 rounded-lg p-4">
          <p className="font-semibold text-indigo-900">Default late policy</p>
          <p>{DEFAULT_LATE_POLICY.description}</p>
        </div>
      </div>
    </div>
  );
};

export default CourseSettings;
